import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

interface StaffMember {
  id: string;
  dealer_id: string;
  full_name: string;
  role: string;
  phone: string;
  is_active: boolean;
  created_at: string;
}

const mockStaff: StaffMember[] = [
  {
    id: "s1",
    dealer_id: "",
    full_name: "Ahmet Yılmaz",
    role: "Garson",
    phone: "0532 111 2233",
    is_active: true,
    created_at: "2025-01-15T10:00:00Z",
  },
  {
    id: "s2",
    dealer_id: "",
    full_name: "Ayşe Demir",
    role: "Aşçı",
    phone: "0533 222 3344",
    is_active: true,
    created_at: "2025-02-01T09:30:00Z",
  },
  {
    id: "s3",
    dealer_id: "",
    full_name: "Mehmet Kaya",
    role: "Komi",
    phone: "0535 333 4455",
    is_active: false,
    created_at: "2025-03-10T14:00:00Z",
  },
  {
    id: "s4",
    dealer_id: "",
    full_name: "Zeynep Çelik",
    role: "Şef Garson",
    phone: "0536 444 5566",
    is_active: true,
    created_at: "2025-04-05T11:00:00Z",
  },
  {
    id: "s5",
    dealer_id: "",
    full_name: "Ali Öztürk",
    role: "Bulaşıkçı",
    phone: "0537 555 6677",
    is_active: true,
    created_at: "2025-05-20T08:00:00Z",
  },
];

export default function DealerAdminStaffPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId;

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState<StaffMember | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<StaffMember | null>(null);

  const [formName, setFormName] = useState("");
  const [formRole, setFormRole] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formActive, setFormActive] = useState(true);

  const fetchStaff = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("staff")
        .select("*")
        .eq("dealer_id", dealerId)
        .order("full_name");

      if (err) throw err;
      if (!data || data.length === 0) {
        setStaff(mockStaff);
      } else {
        setStaff(data);
      }
    } catch (e: any) {
      setError(e.message || "Personel yüklenirken hata oluştu.");
      setStaff(mockStaff);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const filteredStaff = staff.filter((s) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      s.full_name.toLowerCase().includes(q) ||
      s.role.toLowerCase().includes(q) ||
      s.phone.includes(q)
    );
  });

  const activeCount = staff.filter((s) => s.is_active).length;
  const inactiveCount = staff.filter((s) => !s.is_active).length;

  const openAdd = () => {
    setEditingMember(null);
    setFormName("");
    setFormRole("");
    setFormPhone("");
    setFormActive(true);
    setShowModal(true);
  };

  const openEdit = (member: StaffMember) => {
    setEditingMember(member);
    setFormName(member.full_name);
    setFormRole(member.role || "");
    setFormPhone(member.phone || "");
    setFormActive(member.is_active);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMember(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !dealerId) return;

    setSaving(true);
    try {
      const supabase = getSupabase();
      if (editingMember) {
        const { error: updateErr } = await supabase
          .from("staff")
          .update({
            full_name: formName.trim(),
            role: formRole.trim(),
            phone: formPhone.trim(),
            is_active: formActive,
          })
          .eq("id", editingMember.id);
        if (updateErr) throw updateErr;
        logAction(dealerId, user?.id || null, LOG_ACTIONS.STAFF_UPDATE, `"${formName.trim()}" personeli güncellendi`);
      } else {
        const { error: insertErr } = await supabase
          .from("staff")
          .insert({
            dealer_id: dealerId,
            full_name: formName.trim(),
            role: formRole.trim(),
            phone: formPhone.trim(),
            is_active: formActive,
          });
        if (insertErr) throw insertErr;
        logAction(dealerId, user?.id || null, LOG_ACTIONS.STAFF_ADD, `"${formName.trim()}" personeli eklendi`);
      }

      closeModal();
      fetchStaff();
    } catch (e: any) {
      alert(e.message || "Personel kaydedilirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (member: StaffMember) => {
    try {
      const supabase = getSupabase();
      const { error: updateErr } = await supabase
        .from("staff")
        .update({ is_active: !member.is_active })
        .eq("id", member.id);
      if (updateErr) throw updateErr;

      setStaff((prev) =>
        prev.map((s) => (s.id === member.id ? { ...s, is_active: !s.is_active } : s))
      );
      logAction(dealerId, user?.id || null, LOG_ACTIONS.STAFF_UPDATE, `"${member.full_name}" personeli ${!member.is_active ? "aktif" : "pasif"} yapıldı`);
    } catch (e: any) {
      alert(e.message || "Durum güncellenirken hata oluştu.");
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const supabase = getSupabase();
      const { error: delErr } = await supabase
        .from("staff")
        .delete()
        .eq("id", deleteConfirm.id);
      if (delErr) throw delErr;

      setStaff((prev) => prev.filter((s) => s.id !== deleteConfirm.id));
      logAction(dealerId, user?.id || null, LOG_ACTIONS.STAFF_DELETE, `"${deleteConfirm.full_name}" personeli silindi`);
      setDeleteConfirm(null);
    } catch (e: any) {
      alert(e.message || "Personel silinirken hata oluştu.");
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-team-line text-secondary-600"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">Personel Yönetimi</h1>
          </div>
          <p className="text-sm text-foreground-500 mt-0.5">
            Restoran personelini ekleyin, düzenleyin ve yönetin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-add-line"></i>
            Personel Ekle
          </button>
          <button
            onClick={fetchStaff}
            className="flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Yenile
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs text-foreground-400 mb-1">Toplam Personel</p>
          <p className="text-lg font-bold text-foreground-950">{staff.length}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-check-line text-xs text-primary-600"></i>
            </div>
            <p className="text-xs text-foreground-400">Aktif</p>
          </div>
          <p className="text-lg font-bold text-primary-600">{activeCount}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-foreground-100 flex items-center justify-center">
              <i className="ri-pause-circle-line text-xs text-foreground-500"></i>
            </div>
            <p className="text-xs text-foreground-400">Pasif</p>
          </div>
          <p className="text-lg font-bold text-foreground-500">{inactiveCount}</p>
        </div>
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchStaff} className="ml-auto text-accent-700 font-medium cursor-pointer whitespace-nowrap">Tekrar Dene</button>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-xs">
        <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-foreground-400"></i>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Personel ara..."
          className="w-full pl-8 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : filteredStaff.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-xl py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-team-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-foreground-500 font-medium">Personel bulunamadı</p>
          <p className="text-xs text-foreground-400 mt-1 mb-4">İlk personelinizi ekleyerek başlayın</p>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-add-line"></i>
            Personel Ekle
          </button>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Ad Soyad</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Rol</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Telefon</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Durum</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Eklenme</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((member) => (
                  <tr key={member.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          member.is_active ? "bg-primary-100" : "bg-foreground-100"
                        }`}>
                          <i className={`ri-user-line text-sm ${
                            member.is_active ? "text-primary-600" : "text-foreground-400"
                          }`}></i>
                        </div>
                        <span className="font-medium text-foreground-900">{member.full_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                        {member.role || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground-600 text-xs">{member.phone || "-"}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(member)}
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                          member.is_active
                            ? "bg-primary-100 text-primary-700"
                            : "bg-foreground-100 text-foreground-500"
                        }`}
                      >
                        {member.is_active ? "Aktif" : "Pasif"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-foreground-400 text-xs">{formatDate(member.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEdit(member)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors cursor-pointer"
                          title="Düzenle"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(member)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 transition-colors cursor-pointer"
                          title="Sil"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={closeModal}></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-background-50 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center">
                      <i className={editingMember ? "ri-edit-line text-secondary-600" : "ri-user-add-line text-secondary-600"}></i>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-950">
                      {editingMember ? "Personel Düzenle" : "Yeni Personel Ekle"}
                    </h3>
                  </div>
                  <button onClick={closeModal} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Ad Soyad *</label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                      placeholder="Örn: Ahmet Yılmaz"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Rol / Görev</label>
                    <input
                      type="text"
                      value={formRole}
                      onChange={(e) => setFormRole(e.target.value)}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                      placeholder="Örn: Garson, Aşçı, Komi..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Telefon</label>
                    <input
                      type="text"
                      value={formPhone}
                      onChange={(e) => setFormPhone(e.target.value)}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                      placeholder="0532 111 2233"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="staffActive"
                      checked={formActive}
                      onChange={(e) => setFormActive(e.target.checked)}
                      className="w-4 h-4 rounded border-background-200/70 text-primary-500 focus:ring-primary-400 cursor-pointer"
                    />
                    <label htmlFor="staffActive" className="text-sm text-foreground-600 cursor-pointer">Aktif personel</label>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={!formName.trim() || saving}
                      className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {saving ? (
                        <span className="flex items-center justify-center gap-1.5">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Kaydediliyor
                        </span>
                      ) : editingMember ? "Güncelle" : "Ekle"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setDeleteConfirm(null)}></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-background-50 rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-error-warning-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground-950">Personeli Sil</h4>
                  <p className="text-sm text-foreground-500">{deleteConfirm.full_name} personelini silmek istediğinize emin misiniz?</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  İptal
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}